import { _MatTableDataSource, MatTableDataSource } from '@angular/material/table';
import { DateFormatHelperPipe } from 'src/app/pipes/date-format-helper.pipe';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { catchError, finalize, of } from 'rxjs';

import { ActionOption, ColumnConfig, DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { ConfigUserPermissionService } from '../../../services/property-matrixV2/custom-services/userPermission.service';
import { UserManagementService } from '../../../../app/services/property-matrixV2/services/user-management.service';
import { NotificationService } from '../../../../app/shared/services/notification-service/notification.service';
import { AddEditUserDialogComponent } from '../admin-users/add-edit-user-dialog/add-edit-user-dialog.component';
import { UserActionDialogComponent } from '../admin-users/user-action-dialog/user-action-dialog.component';
import { UserDto } from '../../../../app/services/property-matrixV2/models/user-dto';
import { TagsKeyDialogComponent } from './tags-key-dialog/tags-key-dialog.component';
import { EndorsedUsersComponent } from './endorsed-users/endorsed-users.component';
import { RoleDto } from '../../../../app/services/property-matrixV2/models';
import { User } from '../../../models/User';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss']
})
export class AdminUsersComponent implements OnInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(DataTableComponent) dataTable: DataTableComponent;

  loading: boolean = false;
  users: UserDto[] = [];
  listOfUsersAllWithoutFilter: UserDto[] = [];
  listOfUsersAllWithFilter: UserDto[] = [];
  usersRoles: RoleDto[] = [];
  networkUserSelected: boolean = false;
  roles = ['Public', 'Central', 'Extended Professionals', 'Assistants'];

  data: any = [
    { fullName: '', email: '', tags: '', metro: '', verified: false, actions: '', createdDate: '' },
  ];

  columns: ColumnConfig[] = [
    { columnDef: 'fullName', header: 'Full Name', headerAlign: 'left', cellAlign: 'left', width: 30 },
    { columnDef: 'email', header: 'Email', headerAlign: 'left', cellAlign: 'left', width: 15 },
    {
      columnDef: 'tags', header: 'User Role (Tags)', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20,
      headerButton: {
        icon: 'info',
        action: () => this.openTagsDialog(),
        color: 'coral'
      }
    },
    { columnDef: 'metro', header: 'Metro', headerAlign: 'left', isFilterable: true, cellAlign: 'left', width: 20 },
    { columnDef: 'createdDate', header: 'Created Date', headerAlign: 'left', cellAlign: 'left', width: 10 },
    { columnDef: 'verified', header: 'Account Endorsed', isBoolean: true, headerAlign: 'center', isFilterable: true,
      cellAlign: 'center', width: 15, isDisabled: true, booleanChange: (user: UserDto) => this.endorseUser(user) },
    { columnDef: 'actions', header: 'Actions', isAction: true, headerAlign: 'center', cellAlign: 'center', width: 10 }
  ];

  actionOptions: ActionOption[] = [
    { label: 'Archive', action: (user: UserDto) => this.openActionDialog('remove', user) },
    { label: 'Edit', action: (user: UserDto) => this.openEditDialog('edit', user) },
    { label: 'View', action: (user: UserDto) => this.openEditDialog('view', user) },
    { label: 'Endorse', action: (user: UserDto) => this.endorseUser(user) },
    { label: 'Profile', action: (user: UserDto) => this.profileLogin(user) },
  ];

  requiredRolesFull: string[] = [];
  requiredRolesView: string[] = [];
  currentUserRoles: string[] | null | undefined = [];

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private userManagementService: UserManagementService,
    private dateHelper: DateFormatHelperPipe,
    private notificationService: NotificationService,
    private _userPermissionService: ConfigUserPermissionService,
  ) {
    this.sort = new MatSort;
  }

  ngOnInit(): void {
    this.getUserRoles();
    this.getUserDetail();
    this.getUserPermissionList();
    this.refreshData();
  }

  formatDate(date: string): string {
    return this.dateHelper.transform(date);
  }

  refreshData() {
    this.getUsers();
    this.getAllUsers();
  }

  toggleRoleFilter(event: any) {
    const button = event.currentTarget;
    if (button.classList.contains('active')) {
      button.classList.remove('active');
    } else {
      const buttons = document.querySelectorAll('.role-filter-button');
      buttons.forEach(btn => btn.classList.remove('active'));
      button.classList.add('active');
    }
    this.buttonFilter(button.innerText.replace("person", ""));
  }

  toggleNetworkFilter() {
    this.networkUserSelected = !this.networkUserSelected;
  }

  getUsers() {
    this.loading = true;
    this.userManagementService.apiV1UserManagementGetUsersGet().subscribe({
      next: (users: UserDto[]) => {
        this.users = users;
        this.populateTableData(users);
        this.dataTable.dataSource = new MatTableDataSource(this.data);
        this.dataTable.dataSource.sort = this.dataTable.sort;
        this.dataTable.dataSource.paginator = this.dataTable.paginator;
        this.dataTable.dataSource.filterPredicate = (data: any, filter: string) => {
          const searchStr = filter.toLowerCase();
          return data.fullName.toLowerCase().includes(searchStr) ||
            data.email.toLowerCase().includes(searchStr) ||
            data.tags.toLowerCase().includes(searchStr);
        };
      },
      error: (_error: any) => {
        this.notificationService.showErrorMessage('Error', 'Could not fetch users.');
      }
    });
    this.loading = false;
  }

  getAllUsers() {
    this.loading = true;
    this.userManagementService.apiV1UserManagementGetAllUsersWithoutFiltersGet().subscribe({
      next: (users: UserDto[]) => {
        this.listOfUsersAllWithoutFilter = users;

      },
      error: (_error: any) => {
        this.notificationService.showErrorMessage('Error', 'Could not fetch users.');
      }
    });
    this.loading = false;
  }

  async getUserRoles() {
    this.loading = true;
    this.userManagementService.apiV1UserManagementGetRolesGet().subscribe({
      next: (roleList: RoleDto[]) => {
        this.usersRoles = roleList;
      },
      error: (_error: any) => {
        this.notificationService.showErrorMessage('Error', 'Could not fetch users.');
      }
    });
    this.loading = false;
  }

  populateTableData(users: UserDto[]) {
    this.data = users.map(user => ({
      fullName: `${user.name} ${user.surname}`,
      email: user.email,
      tags: user.userRoles?.map(userRole => userRole.roleName).join(', ') || '',
      verified: (user.endorsed == null) ? false : user.endorsed,
      metro: user.metro?.description,
      createdDate: this.formatDate(user.createdDate),
      actions: '',
      id: user.id,
    }));
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataTable.applyFilter(filterValue);
  }

  openAddEditDialog(): void {
    if (this.getActionsForUser().includes('edit')) {
      const dialogAdd = this.dialog.open(AddEditUserDialogComponent, {
        data: { result: null, viewOnly: false },
        width: '30vw',
        height: '75vh'
      });
      dialogAdd.afterClosed().subscribe(result => {
        this.refreshData();
      })
    } else {
      this.notificationService.showWarningMessage("Insufficient role", "Current logged in user does not have edit permissions or role linked.");
    }
  }

  openEditDialog(actionType: string, user: UserDto): void {
    if (actionType == "view") {
      if (user != null) {
        this.userManagementService.apiV1UserManagementGetCurrentUserByIdGet({ id: user.id }).subscribe(result => {
          if (result) {
            const editUserDialog = this.dialog.open(AddEditUserDialogComponent, {
              data: { result, viewOnly: true },
              width: '30vw',
              height: '75vh',
            });
          } else {
            this.notificationService.showErrorMessage('Error', 'Error fetching current user.');
          }
        });
      } else {
        this.notificationService.showErrorMessage('Error', 'No current user data selected.');
      }
    }
    else if (this.getActionsForUser().includes('edit')) {
      if (user != null) {
        this.userManagementService.apiV1UserManagementGetCurrentUserByIdGet({ id: user.id }).subscribe(result => {
          if (result) {
            const editUserDialog = this.dialog.open(AddEditUserDialogComponent, {
              data: { result, viewOnly: false },
              width: '30vw',
              height: '61vh'
            });
            editUserDialog.afterClosed().subscribe(result => {
              this.refreshData();
            });
          } else {
            this.notificationService.showErrorMessage('Error', 'Error fetching current user.');
          }
        });
      } else {
        this.notificationService.showErrorMessage('Error', 'No current user data selected.');
      }
    } else {
      this.notificationService.showWarningMessage("Insufficient role", "Current logged in user does not have edit permissions or role linked.");
    }
  }

  endorseUser(user: UserDto) {
    let User = this.users.find(f => f.id == user.id);
    if (User) {
      let message = "";
      if (User?.endorsed) {
        message = "Are you sure you no longer want to endorse this user?";
      } else {
        message = "Are you sure you want to endorse this user?";
      }
      this.notificationService.showConfirmMessage("Endorse user", message, "Yes", "No").then(confirmResult => {
        if (confirmResult) {
          this.loading = true;
          this.userManagementService.apiV1UserManagementEndorseUserPost({ body: User })
            .pipe(
              catchError((err) =>
                of(this.notificationService.showErrorMessage("Error", "Could not endorse user."))
              ),
              finalize(() => {
                this.loading = false;
                this.refreshData();
              })
            )
            .subscribe((result: boolean | void) => {
              if (result) {
                if (result == true) {
                  this.notificationService.showSuccessMessage("Saved", "Endorsed user completed.");
                } else {
                  this.notificationService.showErrorMessage("Error", "Did not endorse user with errors found.");
                }
              }
            });
        }else {
          this.refreshData();
        }
      });
    } else {
      this.notificationService.showWarningMessage("Warning", "Error endorsing user.");
    }
  }

  private profileLogin(user: UserDto) {
    let User = this.users.find(f => f.id == user.id);
    if (user) {
      const navigationExtras: NavigationExtras = {
        queryParams: {
          "UserProfile": user.id
        }
      };

      this.router.navigate(['admin/profile'], navigationExtras);
    } else {
      this.notificationService.showWarningMessage("Warning", "User not found to continue.");
    }
  }

  openTagsDialog(): void {
    this.dialog.open(TagsKeyDialogComponent, {
      width: '35vw',
      height: '80vh'
    });
  }

  openActionDialog(actionType: string, user: UserDto): void {
    if (this.getActionsForUser().includes('edit')) {
      const dialogRef = this.dialog.open(UserActionDialogComponent, {
        width: '30vw',
        height: '30vh',
        data: { actionType, userDto: user }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result.action === 'unverify' || result.action === 'remove') {
            this.refreshData();
          }
        }
      });
    } else {
      this.notificationService.showWarningMessage("Insufficient role", "Current logged in user does not have edit permissions or role linked.");
    }
  }

  private getUserDetail() {
    let user = sessionStorage.getItem('user');
    if (user != null) {
      let currentUser = JSON.parse(JSON.parse(user)) as User;

      for (let index = 0; index < currentUser.roles.length; index++) {
        var tokenRoles = currentUser.roles[index];
        if (this.currentUserRoles) {
          this.currentUserRoles.push(tokenRoles);
        }
      }
    } else {
      this.loadCurrentUserRole();
    }
  }

  private loadCurrentUserRole(): void {
    this.userManagementService.apiV1UserManagementGetUserTokenGet().subscribe(roleBasedAccessDto => {
      this.currentUserRoles = roleBasedAccessDto.roles;
    });
  }

  private getUserPermissionList(): void {
    this._userPermissionService.getRequiredRoles().subscribe(data => {
      this.requiredRolesFull = data.requiredRolesFull;
      this.requiredRolesView = data.requiredRolesView;
    });
  }

  private getActionsForUser() {
    if (this.hasRequiredRoleFull()) {
      return ['view', 'edit'];
    } else if (this.hasRequiredRoleView()) {
      return ['view'];
    }
    return [];
  }

  private hasRequiredRoleFull(): boolean {
    return this.currentUserRoles?.some(role => this.requiredRolesFull.includes(role)) ?? false;
  }

  private hasRequiredRoleView(): boolean {
    return this.currentUserRoles?.some(role => this.requiredRolesView.includes(role)) ?? false;
  }

  allUsers(): void {
    if (this.listOfUsersAllWithoutFilter) {
      this.listOfUsersAllWithFilter = this.listOfUsersAllWithoutFilter.filter(u => u.isDeleted == false);
      this.populateTableData(this.listOfUsersAllWithFilter);
      this.dataTable.dataSource = new MatTableDataSource(this.data);
      this.dataTable.dataSource.sort = this.dataTable.sort;
      this.dataTable.dataSource.paginator = this.dataTable.paginator;
      this.dataTable.dataSource.filterPredicate = (data: any, filter: string) => {
        const searchStr = filter.toLowerCase();
        return data.fullName.toLowerCase().includes(searchStr) ||
          data.email.toLowerCase().includes(searchStr) ||
          data.tags.toLowerCase().includes(searchStr);
      };
    } else {
      this.notificationService.showWarningMessage("Warning", "There are no users to filter on.");
    }
  }

  archivedUsers(): void {
    if (this.listOfUsersAllWithoutFilter) {
      this.listOfUsersAllWithFilter = this.listOfUsersAllWithoutFilter.filter(u => u.isDeleted == true);
      this.populateTableData(this.listOfUsersAllWithFilter);
      this.dataTable.dataSource = new MatTableDataSource(this.data);
      this.dataTable.dataSource.sort = this.dataTable.sort;
      this.dataTable.dataSource.paginator = this.dataTable.paginator;
      this.dataTable.dataSource.filterPredicate = (data: any, filter: string) => {
        const searchStr = filter.toLowerCase();
        return data.fullName.toLowerCase().includes(searchStr) ||
          data.email.toLowerCase().includes(searchStr) ||
          data.tags.toLowerCase().includes(searchStr);
      };
    } else {
      this.notificationService.showWarningMessage("Warning", "There are no users to filter on.");
    }
  }

  buttonFilter(role: string): void {
    if (role) {
      this.listOfUsersAllWithFilter = this.listOfUsersAllWithoutFilter.filter(u => u.userRoles?.find(ur => ur.roleName == role));
      this.populateTableData(this.listOfUsersAllWithFilter);
      this.dataTable.dataSource = new MatTableDataSource(this.data);
      this.dataTable.dataSource.sort = this.dataTable.sort;
      this.dataTable.dataSource.paginator = this.dataTable.paginator;
      this.dataTable.dataSource.filterPredicate = (data: any, filter: string) => {
        const searchStr = filter.toLowerCase();
        return data.fullName.toLowerCase().includes(searchStr) ||
          data.email.toLowerCase().includes(searchStr) ||
          data.tags.toLowerCase().includes(searchStr);
      };
    } else {
      this.notificationService.showErrorMessage("Error", "Could not get Role.");
    }
  }

  public openEndorsedUsersDialog(): void {
    const dialogRef = this.dialog.open(EndorsedUsersComponent, {
      width: '90vw',
      height: '85vh',
      data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.refreshData();
      }
    });
  }
}
